import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ORIGEM_ALUNO, type OrigemAluno } from '@arena/shared/dominio';
import {
  ROTULO_ORIGEM_ALUNO,
  ROTULO_STATUS_ALUNO,
  TRANSICOES,
  casosPossiveis,
  zAlteracaoDeAluno,
  zCancelamentoDeAluno,
  type CasoDeStatus,
  type FichaDoAluno,
} from '@arena/shared/schemas';
import { alunosApi } from '../api/alunos.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { useSessao } from '../auth/SessaoContext.jsx';
// 10 · turmas
import { AbaPresencasDoAluno, AbaTurmasDoAluno } from '../turmas/AbasDoAluno.jsx';
// 12 · planos
import { AbaFinanceiroDoAluno } from '../planos/AbaFinanceiroDoAluno.jsx';
// 13 · estoque e comandas
import { AbaComandasDoAluno } from '../estoque/AbaComandasDoAluno.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { AbasDeModal, CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Avatar, Botao, Campo, Formulario, Nota, Status } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  dataPorExtenso,
  errosPorCampo,
  idadeTexto,
  instanteCompleto,
  primeiraMensagem,
  SITUACAO_DO_STATUS,
} from './formatos.js';

/**
 * `#m-aluno` — a ficha, com as seis abas.
 *
 * As seis funcionam de ponta a ponta: Dados e LGPD (documento 08),
 * Turmas e Presenças (documento 10), Financeiro (documento 12) e
 * Comandas (documento 13).
 *
 * Duas decisões que DIVERGEM do protótipo estático, de propósito:
 *
 *   Status não é um <select> na aba Dados. "Status muda por caso de uso,
 *   não por edição de campo" é uma das decisões centrais do documento —
 *   um campo livre perderia o motivo obrigatório do cancelamento e o
 *   histórico. Aqui é um selo + os botões que `casosPossiveis` libera.
 *
 *   A anonimização não está escondida: é uma ação própria na aba LGPD,
 *   só para quem tem a ação `anonimizar` (o Dono), com confirmação forte
 *   — o nome completo digitado — e só depois do cadastro cancelado.
 */
export function ModalDoAluno({ id, onFechar }: { id: string; onFechar: () => void }) {
  const consulta = useQuery({
    queryKey: ['alunos', 'ficha', id],
    queryFn: () => alunosApi.ficha(id),
    retry: false,
  });

  if (consulta.isPending) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Carregando o cadastro…" largura="larga">
        <CorpoDeModal>
          <EsqueletoDeCard linhas={4} />
        </CorpoDeModal>
      </Modal>
    );
  }

  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Modal aberto onFechar={onFechar} titulo="Aluno" largura="estreita">
        <CorpoDeModal>
          <Erro
            tipo={
              erro instanceof ErroDeRede
                ? 'rede'
                : erro instanceof ErroDeApi && erro.ehPermissao
                  ? 'permissao'
                  : 'falha'
            }
            detalhe={mensagemDoErro(erro)}
          />
        </CorpoDeModal>
      </Modal>
    );
  }

  return <FichaAberta ficha={consulta.data} onFechar={onFechar} />;
}

const ROTULO_NO_PASSADO: Record<CasoDeStatus, string> = {
  ativar: 'ativado',
  inativar: 'inativado',
  cancelar: 'cancelado',
};

function FichaAberta({ ficha, onFechar }: { ficha: FichaDoAluno; onFechar: () => void }) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();

  const podeEditar = pode('alunos', 'editar');
  const podeAnonimizar = pode('alunos', 'anonimizar');
  const anonimizado = Boolean(ficha.anonimizadoEm);
  // A chave ausente É a resposta da dimensão 3 — não um campo vazio.
  const temSaude = 'observacoesMedicas' in ficha;

  const invalidar = () => cliente.invalidateQueries({ queryKey: ['alunos'] });

  // ===================================================================
  // Dados — edição
  // ===================================================================
  const [nome, setNome] = useState(ficha.nome);
  const [apelido, setApelido] = useState(ficha.apelido ?? '');
  const [telefone, setTelefone] = useState(ficha.telefone ?? '');
  const [nascimento, setNascimento] = useState(ficha.nascimento ?? '');
  const [cpf, setCpf] = useState(ficha.cpf ?? '');
  const [email, setEmail] = useState(ficha.email ?? '');
  const [origem, setOrigem] = useState<OrigemAluno | ''>(ficha.origem ?? '');
  const [contatoNome, setContatoNome] = useState(ficha.contatoEmergenciaNome ?? '');
  const [contatoTelefone, setContatoTelefone] = useState(ficha.contatoEmergenciaTelefone ?? '');
  const [responsavel, setResponsavel] = useState(ficha.responsavelFinanceiro ?? '');
  const [observacoesMedicas, setObservacoesMedicas] = useState(ficha.observacoesMedicas ?? '');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const alterar = useMutation({
    mutationFn: (dados: unknown) => alunosApi.alterar(ficha.id, zAlteracaoDeAluno.parse(dados)),
    onSuccess: invalidar,
  });

  function mudancasPendentes(): Record<string, unknown> {
    const d: Record<string, unknown> = {};
    if (nome !== ficha.nome) d['nome'] = nome;
    if (apelido !== (ficha.apelido ?? '')) d['apelido'] = apelido || null;
    if (telefone !== (ficha.telefone ?? '')) d['telefone'] = telefone;
    if (nascimento !== (ficha.nascimento ?? '')) d['nascimento'] = nascimento;
    if (cpf !== (ficha.cpf ?? '')) d['cpf'] = cpf || null;
    if (email !== (ficha.email ?? '')) d['email'] = email || null;
    if (origem !== (ficha.origem ?? '')) d['origem'] = origem || null;
    if (contatoNome !== (ficha.contatoEmergenciaNome ?? ''))
      d['contatoEmergenciaNome'] = contatoNome || null;
    if (contatoTelefone !== (ficha.contatoEmergenciaTelefone ?? '')) {
      d['contatoEmergenciaTelefone'] = contatoTelefone || null;
    }
    if (responsavel !== (ficha.responsavelFinanceiro ?? ''))
      d['responsavelFinanceiro'] = responsavel || null;
    if (temSaude && observacoesMedicas !== (ficha.observacoesMedicas ?? '')) {
      d['observacoesMedicas'] = observacoesMedicas || null;
    }
    return d;
  }
  const pendencias = mudancasPendentes();
  const sujo = Object.keys(pendencias).length > 0;

  async function salvar() {
    setErros({});
    setErroGeral(null);
    const pedido = zAlteracaoDeAluno.safeParse(pendencias);
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }
    try {
      await alterar.mutateAsync(pedido.data);
      avisar('Cadastro atualizado.');
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  // ===================================================================
  // Ciclo de vida
  // ===================================================================
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [errosStatus, setErrosStatus] = useState<Record<string, string>>({});

  const mudarStatus = useMutation({
    mutationFn: ({ caso, motivo: m }: { caso: CasoDeStatus; motivo?: string }) => {
      if (caso === 'ativar') return alunosApi.ativar(ficha.id, { motivo: m });
      if (caso === 'inativar') return alunosApi.inativar(ficha.id, { motivo: m });
      return alunosApi.cancelar(ficha.id, { motivo: m ?? '' });
    },
    onSuccess: invalidar,
  });

  async function aplicarCaso(caso: CasoDeStatus) {
    setErroGeral(null);
    if (caso === 'cancelar' && !cancelando) {
      setCancelando(true);
      return;
    }
    let motivoValidado = motivo.trim() || undefined;
    if (caso === 'cancelar') {
      const pedido = zCancelamentoDeAluno.safeParse({ motivo });
      if (!pedido.success) {
        setErrosStatus(errosPorCampo(pedido.error.issues));
        return;
      }
      motivoValidado = pedido.data.motivo;
    }
    try {
      await mudarStatus.mutateAsync({ caso, motivo: motivoValidado });
      avisar(`Cadastro ${ROTULO_NO_PASSADO[caso]}.`);
      setCancelando(false);
      setMotivo('');
      setErrosStatus({});
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErrosStatus(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  const casos = casosPossiveis(ficha.status, anonimizado);

  // ===================================================================
  // Consentimentos
  // ===================================================================
  const [erroConsentimento, setErroConsentimento] = useState<string | null>(null);
  const consentir = useMutation({
    mutationFn: () => alunosApi.registrarConsentimento(ficha.id, 'UsoDeImagem'),
    onSuccess: invalidar,
  });
  const revogar = useMutation({
    mutationFn: () => alunosApi.revogarConsentimento(ficha.id, 'UsoDeImagem'),
    onSuccess: invalidar,
  });

  async function alternarImagem(marcar: boolean): Promise<void> {
    setErroConsentimento(null);
    try {
      if (marcar) await consentir.mutateAsync();
      else await revogar.mutateAsync();
      avisar(
        marcar
          ? 'Consentimento de uso de imagem registrado.'
          : 'Consentimento de uso de imagem revogado.',
      );
    } catch (erro) {
      setErroConsentimento(mensagemDoErro(erro));
    }
  }

  // ===================================================================
  // Anonimização
  // ===================================================================
  const [anonimizando, setAnonimizando] = useState(false);
  const [confirmacao, setConfirmacao] = useState('');
  const [erroAnonimizacao, setErroAnonimizacao] = useState<string | null>(null);
  const anonimizar = useMutation({
    mutationFn: (texto: string) => alunosApi.anonimizar(ficha.id, texto),
    onSuccess: invalidar,
  });

  async function confirmarAnonimizacao(): Promise<void> {
    setErroAnonimizacao(null);
    try {
      await anonimizar.mutateAsync(confirmacao);
      avisar('Cadastro anonimizado. Os dados pessoais foram apagados.');
      setAnonimizando(false);
      setConfirmacao('');
    } catch (erro) {
      setErroAnonimizacao(mensagemDoErro(erro));
    }
  }

  // ===================================================================
  // Exportação — "Exportar dados desta pessoa"
  // ===================================================================
  const [exportando, setExportando] = useState(false);

  async function exportar(): Promise<void> {
    setExportando(true);
    try {
      const dados = await alunosApi.exportar(ficha.id);
      baixarJson(`aluno-${ficha.id}.json`, dados);
    } catch (erro) {
      avisar(mensagemDoErro(erro), 'aviso');
    } finally {
      setExportando(false);
    }
  }

  // ===================================================================
  // Render
  // ===================================================================
  const idade = idadeTexto(ficha.idade, ficha.menorDeIdade);
  const subtitulo = [
    `${ROTULO_STATUS_ALUNO[ficha.status]} desde ${dataPorExtenso(ficha.desde)}`,
    anonimizado ? 'cadastro anonimizado' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const consentimentos = ficha.consentimentos;
  const imagemVigente = Boolean(consentimentos.imagem && !consentimentos.imagem.revogadoEm);

  const conteudoDados = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Status situacao={SITUACAO_DO_STATUS[ficha.status]}>
          {ROTULO_STATUS_ALUNO[ficha.status]}
        </Status>
        {podeEditar &&
          !anonimizado &&
          casos.map((caso) => (
            <Botao
              key={caso}
              variante="contorno"
              pequeno
              onClick={() => void aplicarCaso(caso)}
              disabled={mudarStatus.isPending}
            >
              {TRANSICOES[caso].rotulo}
            </Botao>
          ))}
      </div>

      {cancelando && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 12,
            background: 'var(--bg)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-sm)',
          }}
        >
          <Campo rotulo="Motivo do cancelamento" erro={errosStatus['motivo']}>
            <textarea
              rows={2}
              placeholder="Por que o cadastro foi cancelado?"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </Campo>
          <div style={{ display: 'flex', gap: 8 }}>
            <Botao
              variante="fantasma"
              pequeno
              onClick={() => {
                setCancelando(false);
                setMotivo('');
                setErrosStatus({});
              }}
            >
              Voltar
            </Botao>
            <Botao
              pequeno
              onClick={() => void aplicarCaso('cancelar')}
              disabled={mudarStatus.isPending}
            >
              {mudarStatus.isPending ? 'Cancelando…' : 'Confirmar cancelamento'}
            </Botao>
          </div>
        </div>
      )}

      <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
        <Campo rotulo="Nome completo" erro={erros['nome']}>
          <input
            value={nome}
            disabled={!podeEditar || anonimizado}
            onChange={(e) => setNome(e.target.value)}
          />
        </Campo>
        <Campo rotulo="Apelido (chamada e comanda)" erro={erros['apelido']}>
          <input
            value={apelido}
            disabled={!podeEditar || anonimizado}
            onChange={(e) => setApelido(e.target.value)}
          />
        </Campo>
        <Campo rotulo="Telefone" erro={erros['telefone']}>
          <input
            value={telefone}
            disabled={!podeEditar || anonimizado}
            onChange={(e) => setTelefone(e.target.value)}
          />
        </Campo>
        <Campo rotulo="Data de nascimento" erro={erros['nascimento']} ajuda={idade ?? undefined}>
          <input
            type="date"
            value={nascimento}
            disabled={!podeEditar || anonimizado}
            onChange={(e) => setNascimento(e.target.value)}
          />
        </Campo>
        <Campo rotulo="CPF" erro={erros['cpf']}>
          <input
            value={cpf}
            disabled={!podeEditar || anonimizado}
            onChange={(e) => setCpf(e.target.value)}
          />
        </Campo>
        <Campo rotulo="E-mail" erro={erros['email']}>
          <input
            value={email}
            disabled={!podeEditar || anonimizado}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Campo>
        <Campo rotulo="Contato de emergência" erro={erros['contatoEmergenciaNome']}>
          <input
            value={contatoNome}
            disabled={!podeEditar || anonimizado}
            onChange={(e) => setContatoNome(e.target.value)}
          />
        </Campo>
        <Campo rotulo="Telefone do contato" erro={erros['contatoEmergenciaTelefone']}>
          <input
            value={contatoTelefone}
            disabled={!podeEditar || anonimizado}
            onChange={(e) => setContatoTelefone(e.target.value)}
          />
        </Campo>
        <Campo rotulo="Como conheceu a arena" erro={erros['origem']}>
          <select
            value={origem}
            disabled={!podeEditar || anonimizado}
            onChange={(e) => setOrigem(e.target.value as OrigemAluno | '')}
          >
            <option value="">Não informado</option>
            {ORIGEM_ALUNO.map((o) => (
              <option key={o} value={o}>
                {ROTULO_ORIGEM_ALUNO[o]}
              </option>
            ))}
          </select>
        </Campo>
      </Formulario>

      {temSaude && (
        <Campo rotulo="Observações médicas e restrições" erro={erros['observacoesMedicas']}>
          <textarea
            rows={2}
            value={observacoesMedicas}
            disabled={!podeEditar || anonimizado}
            onChange={(e) => setObservacoesMedicas(e.target.value)}
          />
        </Campo>
      )}

      <Campo
        rotulo="Responsável financeiro"
        erro={erros['responsavelFinanceiro']}
        ajuda={ficha.menorDeIdade ? 'Obrigatório — o aluno é menor de idade.' : undefined}
      >
        <input
          value={responsavel}
          disabled={!podeEditar || anonimizado}
          onChange={(e) => setResponsavel(e.target.value)}
        />
      </Campo>

      {erroGeral && (
        <div className="aviso conflito" role="alert">
          <Icone nome="info" />
          <div>{erroGeral}</div>
        </div>
      )}
    </>
  );

  const conteudoLgpd = (
    <>
      <label className="consent">
        <input
          className="ck"
          type="checkbox"
          checked={Boolean(consentimentos.termo)}
          disabled
          readOnly
        />
        <span>
          Termo de uso e política de privacidade
          <small>
            {consentimentos.termo
              ? `Aceito em ${instanteCompleto(consentimentos.termo.aceitoEm, ficha.fuso)} · IP ${consentimentos.termo.ip ?? '—'} · versão ${consentimentos.termo.versao}`
              : 'Pendente — cadastro sem aceite registrado.'}
          </small>
        </span>
      </label>

      <label className="consent">
        <input
          className="ck"
          type="checkbox"
          checked={imagemVigente}
          disabled={!podeEditar || anonimizado || consentir.isPending || revogar.isPending}
          onChange={(e) => void alternarImagem(e.target.checked)}
        />
        <span>
          Uso de imagem em redes sociais
          <small>
            {consentimentos.imagem
              ? imagemVigente
                ? `Aceito em ${instanteCompleto(consentimentos.imagem.aceitoEm, ficha.fuso)} · revogável a qualquer momento`
                : `Revogado em ${instanteCompleto(consentimentos.imagem.revogadoEm as string, ficha.fuso)}`
              : 'Ainda não autorizado. Consentimento separado e revogável a qualquer momento.'}
          </small>
        </span>
      </label>

      {erroConsentimento && (
        <div className="aviso conflito" role="alert">
          <Icone nome="info" />
          <div>{erroConsentimento}</div>
        </div>
      )}

      <Nota>
        No cancelamento o cadastro é <b>anonimizado</b>, não excluído: o histórico financeiro é
        preservado por obrigação legal e os dados pessoais deixam de ser identificáveis.
      </Nota>

      <Botao
        variante="contorno"
        onClick={() => void exportar()}
        disabled={exportando}
        style={{ alignSelf: 'flex-start' }}
      >
        {exportando ? 'Exportando…' : 'Exportar dados desta pessoa'}
      </Botao>

      {anonimizado && (
        <p className="sub">
          Cadastro anonimizado em {instanteCompleto(ficha.anonimizadoEm as string, ficha.fuso)}.
        </p>
      )}

      {podeAnonimizar && ficha.status === 'Cancelado' && !anonimizado && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!anonimizando ? (
            <Botao
              variante="contorno"
              onClick={() => setAnonimizando(true)}
              style={{ alignSelf: 'flex-start', borderColor: 'var(--red)', color: 'var(--red-lt)' }}
            >
              Anonimizar cadastro
            </Botao>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: 12,
                background: 'var(--bg)',
                border: '1px solid var(--red)',
                borderRadius: 'var(--r-sm)',
              }}
            >
              <p className="sub">
                Ação IRREVERSÍVEL: apaga os dados pessoais e a ficha de saúde, mantém o id e o
                histórico financeiro. Para confirmar, digite o nome completo do aluno —{' '}
                <b>{ficha.nome}</b>.
              </p>
              <input
                placeholder="Digite o nome completo para confirmar"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
              />
              {erroAnonimizacao && (
                <div className="help" style={{ color: 'var(--red-lt)' }} role="alert">
                  {erroAnonimizacao}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <Botao
                  variante="fantasma"
                  pequeno
                  onClick={() => {
                    setAnonimizando(false);
                    setConfirmacao('');
                    setErroAnonimizacao(null);
                  }}
                >
                  Cancelar
                </Botao>
                <Botao
                  pequeno
                  onClick={() => void confirmarAnonimizacao()}
                  disabled={!confirmacao.trim() || anonimizar.isPending}
                >
                  {anonimizar.isPending ? 'Anonimizando…' : 'Confirmar anonimização'}
                </Botao>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <Modal
      aberto
      onFechar={onFechar}
      marca={<Avatar nome={ficha.nome} tamanho={44} />}
      titulo={ficha.nome}
      descricao={subtitulo}
      largura="larga"
      rodape={
        <>
          <span className="left">Toda leitura desta ficha é registrada em auditoria</span>
          <Botao variante="fantasma" onClick={onFechar}>
            Fechar
          </Botao>
          {podeEditar && !anonimizado && (
            <Botao onClick={() => void salvar()} disabled={!sujo || alterar.isPending}>
              {alterar.isPending ? 'Salvando…' : 'Salvar alterações'}
            </Botao>
          )}
        </>
      }
    >
      <AbasDeModal
        abas={[
          { chave: 'dados', rotulo: 'Dados', conteudo: conteudoDados },
          // 10 · turmas
          {
            chave: 'turmas',
            rotulo: 'Turmas',
            conteudo: <AbaTurmasDoAluno alunoId={ficha.id} />,
          },
          // 12 · planos
          {
            chave: 'financeiro',
            rotulo: 'Financeiro',
            conteudo: <AbaFinanceiroDoAluno alunoId={ficha.id} alunoNome={ficha.nome} />,
          },
          // 10 · turmas
          {
            chave: 'presencas',
            rotulo: 'Presenças',
            conteudo: <AbaPresencasDoAluno alunoId={ficha.id} />,
          },
          // 13 · estoque e comandas
          {
            chave: 'comandas',
            rotulo: 'Comandas',
            conteudo: <AbaComandasDoAluno alunoId={ficha.id} />,
          },
          { chave: 'lgpd', rotulo: 'LGPD', conteudo: conteudoLgpd },
        ]}
      />
    </Modal>
  );
}

/** "Exportar dados desta pessoa": baixa o JSON no navegador de quem pediu. */
function baixarJson(nomeDoArquivo: string, dados: unknown): void {
  const blob = new Blob([JSON.stringify(dados, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeDoArquivo;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
